<!-- EditBusiness.vue -->
<template>
  <div class="container">
    <router-link to="/user/vendor-profile-overview" class="back-button">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.5025 16.5984L7.06914 11.1651C6.42747 10.5234 6.42747 9.47344 7.06914 8.83177L12.5025 3.39844"
          stroke="#555C74"
          stroke-width="1.6"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      Back
    </router-link>
    <!-- Navigation Tabs -->
    <div class="nav-tabs">
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          {{ tab.name }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading">Loading...</div>

      <!-- Business Info Tab -->
      <div v-if="!loading && !error && activeTab === 1">
        <h1>Edit business info</h1>
        <p class="subtitle">Update basic information about your business.</p>

        <div class="section">
          <div
            class="section-header"
            @click="toggleSection(1, 'businessDetails')"
          >
            <h2>Business details</h2>
            <span class="toggle-icon">
              <svg
                v-if="activeSectionTab1 === 'businessDetails'"
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
              <svg
                v-else
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </div>
          <div
            v-show="activeSectionTab1 === 'businessDetails'"
            class="section-content"
          >
            <div class="form-group">
              <label>Business name</label>
              <input
                type="text"
                v-model="businessName"
                placeholder="Enter business name"
              />
            </div>
            <div class="form-group">
              <label>Address</label>
              <input
                type="text"
                v-model="businessAddress"
                placeholder="Enter business address"
              />
            </div>

            <div class="form-group">
              <label for="about-business" class="form-label"
                >About your business</label
              >
              <textarea
                id="about-business"
                class="form-textarea"
                v-model="aboutDescription"
                placeholder="Give a brief intro about what you do"
                maxlength="5000"
              ></textarea>
            </div>

            <button
              disabled
              class="update-btn"
              @click="updateBusinessInfo()"
            
            >
              {{ saving ? "Saving..." : "Update" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Service Details Tab -->
      <div v-if="!loading && !error && activeTab === 2">
        <h1>Edit service details</h1>
        <p class="subtitle">Update the services you offer.</p>

        <div class="section">
          <div
            class="section-header"
            @click="toggleSection(2, 'servicesOffered')"
          >
            <h2>Services offered</h2>
            <span class="toggle-icon">
              <svg
                v-if="activeSectionTab2 === 'servicesOffered'"
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
              <svg
                v-else
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </div>
          <div
            v-show="activeSectionTab2 === 'servicesOffered'"
            class="section-content"
          >
            <div class="form-group">
              <label>Service type</label>
              <select
                v-model="serviceID"
                id="services"
                class="dropdown-input"
              >
                <option value="" disabled selected>
                  Select services offered
                </option>
                <option
                  v-for="service in services"
                  :key="service.id"
                  :value="service.id"
                >
                  {{ service.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="minimumRate" class="form-label"
                >Minimum rate <span class="required">*</span></label
              >
              <input
                type="text"
                id="minimumRate"
                class="form-input"
                v-model="formattedAmount"
                @input="handleInput"
                placeholder="Enter minimum rate"
              />
            </div>
          </div>
        </div>

        <button class="update-btn" @click="serviceInfo()" :disabled="saving">
          {{ saving ? "Saving..." : "Update" }}
        </button>
      </div>

      <!-- Additional Business Details Tab -->
      <div v-if="!loading && !error && activeTab === 3">
        <h1>Additional business details</h1>
        <p class="subtitle">
          This step is optional, but updating more details can help attract more
          customers.
        </p>

        <div class="section">
          <div
            class="section-header"
            @click="toggleSection(3, 'certifications')"
          >
            <h2>Certification details</h2>
            <span class="toggle-icon">
              <svg
                v-if="activeSectionTab3 === 'certifications'"
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
              <svg
                v-else
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </div>
          <div
            v-show="activeSectionTab3 === 'certifications'"
            class="section-content"
          >
            <p class="section-subtitle">
              Have any professional certifications? Let potential clients know
            </p>
            <div class="form-group">
              <label>Certification name</label>
              <input
                type="text" 
                v-model="certificationName"
                placeholder="Enter certification name"
              />
              <!-- v-model="certName" -->
            </div>
            <div class="form-group">
              <label>Issuing organization</label>
              <input
                type="text"
                v-model="issuingOrg"
                placeholder="Enter issuing organization
                "
              />
            </div>
            <div class="form-group">
              <label>Date obtained</label>

              <div class="date-picker-wrapper">
                <!-- Input field -->
                <input
                  type="text"
                  @focus="showDatePicker = true"
                  readonly
                  v-model="certificationDate"
                  class="date-input"
                />

                <!-- Calendar Icon -->
                <span class="calendar-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 2v2M16 2v2M3 8h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>

                <!-- Hidden Date Picker -->
                <input
                  type="date"
                  v-model="certificationDate"
                  ref="datePicker"
                  class="hidden-date-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-header" @click="toggleSection(3, 'workSamples')">
            <h2>Work samples</h2>
            <span class="toggle-icon">
              <svg
                v-if="activeSectionTab3 === 'workSamples'"
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
              <svg
                v-else
                class="icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </div>
          <div
            v-show="activeSectionTab3 === 'workSamples'"
            class="section-content"
          >
            <div class="form-group">
              <label>Work summary</label>
              <input
                type="text"
                v-model="summaryWork"
                placeholder="Changed old wc to new wc"
              />
            </div>

            <label>Provide images</label>
              <div class="image-upload-section">
                <div
                  class="image-upload-box"
                  :class="[uploadingWorkSamples ? 'uploaadingWork' : '']"
                  @click="triggerFileUpload"
                >
                  <span class="plus-icon">
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.5 5V19"
                        stroke="#555C74"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5.5 12H19.5"
                        stroke="#555C74"
                        stroke-width="1.5"
                      />
                    </svg>
                  </span>
                  <p>Add image</p>
                </div>
                <input
                  :disabled="uploadingWorkSamples"
                  type="file"
                  ref="fileInput"
                  @change="handleFileUploadWorkSamples"
                  accept="image/*"
                  hidden
                />
                <!-- handleImageSelection -->

                <div
                  v-for="(item, index) in workImages"
                  :key="index"
                  class="image-preview"
                >
                  <img
                    :src="item"
                    alt="Uploaded image"
                    class="uploaded-image"
                  />
                  <button class="delete-btn" @click="removeNewImage(index)">
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 36 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="36"
                        height="36"
                        rx="8"
                        fill="white"
                        fill-opacity="0.5"
                      />
                      <rect
                        x="0.5"
                        y="0.5"
                        width="35"
                        height="35"
                        rx="7.5"
                        stroke="white"
                        stroke-opacity="0.3"
                      />
                      <path
                        d="M25.5 12.9831C22.725 12.7081 19.9333 12.5664 17.15 12.5664C15.5 12.5664 13.85 12.6497 12.2 12.8164L10.5 12.9831"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M15.082 12.143L15.2654 11.0513C15.3987 10.2596 15.4987 9.66797 16.907 9.66797H19.0904C20.4987 9.66797 20.607 10.293 20.732 11.0596L20.9154 12.143"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M23.7057 15.6172L23.1641 24.0089C23.0724 25.3172 22.9974 26.3339 20.6724 26.3339H15.3224C12.9974 26.3339 12.9224 25.3172 12.8307 24.0089L12.2891 15.6172"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M16.6055 21.75H19.3805"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M15.918 18.418H20.0846"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <!-- Add Another Button
              <div v-if="workSampleFile" >
                <div v-for="(item, index) in workImages" :key="item" class="add-another-wrap">
                  <button class="add-another-btn">
                    {{ item }}
                  </button>

                  <span @click="removeNewImage(index)">
                    <b> X </b>
                  </span>
                </div>
              </div> -->

              <div>
                <button
                  class="btn uploadWork"
                  @click="submitWorkSummaryFiles()"
                  :disabled="uploadingFiles"
                  v-if="workImages.length > 0"
                >
                  Upload work
                  <span v-if="uploadingFiles"
                    ><i class="fa fa-spinner fa-spin"></i
                  ></span>
                </button>
              </div>
          </div>
        </div>

        <button
          class="update-btn"
          @click="additionalDetails()"
          :disabled="saving"
        >
          {{ saving ? "Saving..." : "Update" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import setVendorProfile from "../services/vendorprofile";
import partners from "../services/partners";
export default {
  name: "EditBusiness",
  data() {
    return {
      // Tab state
      activeTab: 1, // Default to "Business info"
      tabs: [
        { id: 1, name: "Business info" },
        { id: 2, name: "Service details" },
        { id: 3, name: "Additional business details" },
      ],

      // Active section states for each tab
      activeSectionTab1: "businessDetails", // Default open section for Tab 1
      activeSectionTab2: "servicesOffered", // Default open section for Tab 2
      activeSectionTab3: "certifications", // Default open section for Tab 3
      services: [
        { id: 1, name: "Plumbing" },
        { id: 2, name: "Electrical Work" },
        { id: 3, name: "Painting" },
      ],
      selectedService: "",
      businessName: "",
      businessAddress: "",
      aboutDescription: "",
      service: "",
      rate: "",
      amount: null,
      certificationName: "",
      issuingOrg: "",
      dateObtained: "",
      certName: "",
      certificationDate: "",
      workSamples: [],
      workSummary: "",
      minimumRate: "",
      serviceID: "",
      formattedAmount: "",

      // API states
      loading: false,
      error: null,
      saving: false,
      uploadingWorkSamples: false,
      workSampleServerFileName: "",
      workImages: [],
      workSampleFile: "",
      newSummary: "",
      newImages: [],
      uploadingFiles: false,
      summaryWork: "",

    };
  },
  methods: {
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    removeNewImage(index) {
      this.workImages.splice(index, 1);
    },
    editSample(index) {
      this.newSummary = this.workSamples[index].summary;
      this.newImages = [...this.workSamples[index].images];
      this.workSamples.splice(index, 1);
      this.showNewSampleForm = true;
    },
    handleInput(e) {
      const raw = e.target.value.replace(/[^\d]/g, "");
      const number = Number(raw);
      this.amount = isNaN(number) ? null : number;

      this.formattedAmount = raw ? `₦${number.toLocaleString()}` : "";
      // console.log("amount", this.formattedAmount);
    },

    handleFileUploadWorkSamples(event) {
      this.uploadingWorkSamples = true;
      this.workSampleFile = event.target.files[0];
      // const file = event.target.files[0];
      this.workSampleFileName = this.workSampleFile.name;

      if (!this.workSampleFile) {
        this.uploadingWorkSamples = false;
        // this.$toast.success("success");
        return this.$toast.error("select a file");
      }
      if (
        this.workSampleFile.type === "application/pdf" ||
        this.workSampleFile.type === "image/jpeg" ||
        this.workSampleFile.type === "image/jpg" ||
        this.workSampleFile.type === "image/png" ||
        this.workSampleFile.type === "image/gif" ||
        this.workSampleFile.type === "application/pdf" ||
        this.workSampleFile.type === "svg+xml"
      ) {
        this.uploadingWorkSamples = true;
      } else {
        this.uploadingWorkSamples = false;
        this.workSampleFile = "";
        return this.$toast.error("select an appropraite file");
      }

      // Handle the uploaded file here
      const sizeInMb = this.workSampleFile.size / (1024 * 1024).toFixed(3);
      if (sizeInMb > 5) {
        this.uploadingWorkSamples = false;
        return this.$toast.error("File size more than 5MB");
      }
      let formData = new FormData();
      formData.append("file", this.workSampleFile);
      formData.append("upload_type", "work_samples");
      partners
        .uploadVendorWorkSamples(formData)
        .then((res) => {
          this.uploadingWorkSamples = false;
          if (res.success) {
            this.workSampleServerFileName = res.filename;

            this.workImages.push(res.url);
            this.$toast.success("SUCCESS");
          } else {
            // this.$toast.error("ERROR");
            this.$toast.error(res.error);
          }
        })
        .catch((err) => {
          console.log(err);
          this.uploadingWorkSamples = false;
        });
    },

    async submitWorkSummaryFiles() {
      this.uploadingFiles = true;
      try {
        if (this.workImages.length <= 0) {
          this.uploadingFiles = false;
          return this.$toast.error("No file uploaded");
        }
        if (this.summaryWork === "") {
          this.uploadingFiles = false;
          return this.$toast.error("Enter work summary");
        }
        const body = {
          images: this.workImages,
          work_summary: this.summaryWork,
        };
        await partners
          .submitWorksummaryandfiles(JSON.stringify(body))
          .then((res) => {
            this.uploadingFiles = false;
            if (res.success) {
              this.$toast.success("SUCCESS");
            }
            if (res.error) {
              this.$toast.error(res.error);
            }
          })
          .catch((err) => {
            console.log(err);
            this.uploadingFiles = false;
          });
      } finally {
        // console.log("closed")
      }
    },

    updateServiceID(selectedServiceName) {
      const service = this.services.find((s) => s.name === selectedServiceName);
      this.multiFormData.serviceID = service ? service.id : null;
    },
    toggleSection(tab, section) {
      const tabKey = `activeSectionTab${tab}`;
      if (this[tabKey] === section) {
        this[tabKey] = null; // Close the section
      } else {
        this[tabKey] = section; // Open the clicked section, close the other
      }
    },

    async updateBusinessInfo() {
      this.saving = true;

      const body = {
        business_name: this.businessName,
        business_address: this.businessAddress,
        about_business: this.aboutDescription,
      };

      await setVendorProfile
        .vendorProfileStepOne(JSON.stringify(body))
        .then((res) => {
          if (res.success) {
            this.$toast.success("Profile updated");

            this.$router.push("/user/vendor-profile-overview");
          } else {
            this.$toast.error("Form not submitted");
          }
        })
        .catch((error) => {
          console.log("Error submitting data:", error);
          this.$toast.error("Error submitting");
        });
    },

    async serviceInfo() {
      this.saving = true;

      const body = {
        service_id: this.serviceID,

        minimum_rate: this.formattedAmount,
      };

      await setVendorProfile
        .vendorProfileStepTwo(JSON.stringify(body))
        .then((res) => {
          this.saving = false;
          if (res.success) {
            this.$toast.success("Profile updated");

            this.$router.push("/user/vendor-profile-overview");
          } else {
            this.$toast.error("Form not submitted");
          }
        })
        .catch((error) => {
          this.saving = false;
          console.log("Error submitting data:", error);
          this.$toast.error("Error submitting");
        });
    },

    async additionalDetails() {
      this.saving = true;

      const body = {
        certification_name: this.certificationName,
        certification_issuing_body: this.issuingOrg,
        certification_date_obtained: this.certificationDate,
        work_summary: this.workSummary,
        // certification_date_obtained: this.dateObtained,
      };

      await setVendorProfile
        .vendorProfileStepThree(JSON.stringify(body))
        .then((res) => {
          if (res.success) {
            this.$toast.success("Profile updated");

            this.$router.push("/user/vendor-profile-overview");
          } else {
            this.$toast.error("Form not submitted");
          }
        })
        .catch((error) => {
          console.log("Error submitting data:", error);
          this.$toast.error("Error submitting");
        });
    },
  },

  created() {
    this.loading = true;
    this.error = null;

    let userID = this.$store.getters.getUser.id;
    try {
      setVendorProfile.vendorProfileOverview(userID).then((data) => {
        // console.log("vendor data edit", data);
        (this.businessName = data.main_data.business_name),
          (this.businessAddress = data.main_data.business_address),
          (this.aboutDescription = data.main_data.about_business),
          (this.serviceID = data.main_data.service_id),
          (this.certificationName = data.main_data.certification_name),
          (this.issuingOrg = data.main_data.certification_issuing_body),
          (this.certificationDate = data.main_data.certification_date_obtained),
          (this.summaryWork = data.uploads[0].work_summary),
          (this.formattedAmount = data.main_data.minimum_rate)
      });
    } catch (error) {
      console.log("Error", error);
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
/* EVERYTHING IN THIS COMPONENT SHOULD USE THE LATO FONT */
* {
  font-family: "Lato", sans-serif;
}

/* Base Styles */
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 8px;
}

.nav-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: hsla(0, 0%, 98%, 1);
  border: 1px solid hsla(210, 5%, 92%, 1);
  border-radius: 10px;
  padding: 15px 10px;
}

.back-button {
  background: none;
  font-weight: 600;
  font-size: 14px;
  border: none;
  color: hsla(220, 8%, 35%, 1);
  cursor: pointer;
  margin-bottom: 10px;
}

.tabs {
  display: flex;
  gap: 10px;
}

.tab {
  padding: 8px 16px;
  background: none;
  border-radius: 6px;
  font-size: 14px;
  color: hsla(226, 15%, 39%, 1);
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;
}

.tab.active {
  background-color: hsla(0, 0%, 100%, 1);
  color: #1f2937;
  font-weight: 500;
}

.tab:hover {
  background-color: hsla(0, 0%, 100%, 1);
}

.main-content {
  background-color: #fff;
}

h1 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
  color: hsla(228, 31%, 25%, 1);
}

.subtitle {
  color: hsla(226, 15%, 39%, 1);
  font-weight: 400;
  font-size: 14px;
  margin-bottom: 30px;
}

.section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin: 20px 0;
  padding: 15px 10px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  cursor: pointer;
}

h2 {
  font-size: 18px;
  margin: 0;
  color: hsla(228, 31%, 25%, 1);
  font-weight: 500;
}

.toggle-icon {
  display: flex;
  align-items: center;
}

.icon {
  transition: transform 0.3s ease;
}

.section-content {
  padding: 15px;
}

.section-subtitle {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  font-size: 14px;
  color: hsla(226, 15%, 39%, 1);
  margin-bottom: 5px;
}

input {
  width: 100%;
  padding: 10px;
  border: 1px solid hsla(216, 6%, 85%, 1);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  color: hsla(226, 15%, 39%, 1);
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
}
.dropdown-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid hsla(216, 6%, 85%, 1);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 400;
  color: hsla(226, 15%, 39%, 1);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.2788 5.96484L8.93208 10.3115C8.41875 10.8248 7.57875 10.8248 7.06542 10.3115L2.71875 5.96484' stroke='%23555C74' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
}

textarea {
  padding: 10px;
  min-height: 150px;
  width: 100%;
  border: 1px solid hsla(216, 6%, 85%, 1);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  color: hsla(226, 15%, 39%, 1);
  resize: none;
}

.date-picker-wrapper {
  position: relative;
}

.calendar-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #6b7280;
}

.uploadWork {
  border: 1px solid hsla(226, 15%, 39%, 1);
  color: hsla(226, 15%, 39%, 1);
  font-family: Lato;
  font-size: 16px;
  min-height: 50px;
  margin-top: 40px;
}

/* Hidden native date picker */
.hidden-date-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.update-btn {
  background-color: hsla(227, 100%, 46%, 1);
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  width: max-content;
  display: block;
  margin-left: auto;

  transition: background-color 0.3s;
}

.update-btn:hover {
  background-color: #1d4ed8;
}

.update-btn:disabled {
  background-color: #747474;
  cursor: not-allowed;
}

.loading,
.error {
  text-align: center;
  padding: 20px;
  color: #6b7280;
}

.error {
  color: #e80d0d;
}

/* Responsive Design */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }

  h1 {
    font-size: 20px;
  }

  .tabs {
    flex-wrap: wrap;
    gap: 8px;
  }

  .tab {
    font-size: 12px;
    padding: 6px 12px;
  }

  .update-btn {
    font-size: 14px;
    padding: 8px 16px;
  }

  .back-link {
    font-size: 12px;
  }

  .subtitle,
  .section-subtitle {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 10px;
  }

  h1 {
    font-size: 18px;
  }

  .subtitle,
  .section-subtitle {
    font-size: 12px;
  }

  .tab {
    font-size: 11px;
    padding: 5px 10px;
  }

  input {
    font-size: 12px;
    padding: 8px;
  }

  .update-btn {
    font-size: 12px;
    padding: 6px 12px;
  }

  .section-header {
    padding: 10px;
  }

  h2 {
    font-size: 14px;
  }
}

.image-upload-section {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.image-upload-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 150px;
  height: 150px;
  border: 2px dashed hsla(210, 5%, 92%, 1);
  border-radius: 6px;
  cursor: pointer;
}

@keyframes dashmove {
  from {
    border-image-source: repeating-linear-gradient(
      to right,
      hsla(210, 5%, 92%, 1) 0,
      hsla(210, 5%, 92%, 1) 10px,
      transparent 10px,
      transparent 20px
    );
  }
  to {
    border-image-source: repeating-linear-gradient(
      to right,
      hsla(210, 5%, 92%, 1) 10px,
      hsla(210, 5%, 92%, 1) 20px,
      transparent 20px,
      transparent 30px
    );
  }
}

.plus-icon {
  font-size: 20px;
  font-weight: bold;
}

.add-another-wrap {
  position: relative;
  width: max-content;
}

.add-another-wrap span {
  position: absolute;
  top: 10px;
  right: -20px;
  color: hsla(221, 11%, 29%, 1);
  padding: 5px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  border: 1px solid hsla(221, 11%, 29%, 1);
  border-radius: 50% !important;
  cursor: pointer;
}

.add-another-btn {
  margin-top: 20px;
  padding: 8px 12px;
  background: hsla(0, 0%, 100%, 1);
  color: hsla(221, 11%, 29%, 1);
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 6px;
  border: 1px solid hsla(216, 6%, 85%, 1);
  cursor: pointer;
}

/* SPINNER */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid hsla(227, 100%, 46%, 1);
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.image-preview-section {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.image-preview {
  position: relative;
  width: 150px;
  height: 150px;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 5px;
}

.delete-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 5px;
}

.uploaadingWork {
  border-image: repeating-linear-gradient(
      to right,
      #3498db 0,
      #3498db 10px,
      transparent 10px,
      transparent 20px
    )
    10;
  animation: dashmove 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
